export type Lang = 'nl' | 'en';

export const LANGS: Lang[] = ['nl', 'en'];

/** Page keys shared between both languages. */
export type PageKey =
  | 'visualisatie'
  | 'statement'
  | 'recent'
  | 'nieuwwerk'
  | 'media'
  | 'bio'
  | 'contact'
  | 'nieuwsbrief';

/** Slug (without language prefix, without leading slash) per language. */
export const SLUGS: Record<PageKey, Record<Lang, string>> = {
  visualisatie: { nl: 'visualisatie', en: 'visualize' },
  statement: { nl: 'statement', en: 'statement' },
  recent: { nl: 'netwerk', en: 'network' },
  nieuwwerk: { nl: 'home', en: 'home' },
  media: { nl: 'media', en: 'media' },
  bio: { nl: 'cv', en: 'cv' },
  contact: { nl: 'contact', en: 'contact' },
  nieuwsbrief: { nl: 'nieuwsbrief', en: 'newsletter' },
};

/** Absolute in-app path for a page in a given language. */
export function pathFor(key: PageKey, lang: Lang, rest?: string): string {
  const slug = SLUGS[key][lang];
  const prefix = lang === 'en' ? '/en' : '';
  const tail = [slug, rest].filter(Boolean).join('/');
  const path = tail ? `${prefix}/${tail}` : prefix || '/';
  return path;
}

export function langFromPath(pathname: string): Lang {
  return pathname === '/en' || pathname.startsWith('/en/') ? 'en' : 'nl';
}

/** Resolve which page a pathname belongs to (plus any trailing segment). */
export function matchPath(pathname: string): { key: PageKey; lang: Lang; rest?: string } | null {
  const lang = langFromPath(pathname);
  let rel = lang === 'en' ? pathname.replace(/^\/en/, '') : pathname;
  rel = rel.replace(/^\/+|\/+$/g, '');
  if (!rel) return { key: 'nieuwwerk', lang };
  const [first, ...others] = rel.split('/');
  const entry = (Object.keys(SLUGS) as PageKey[]).find((k) => SLUGS[k][lang] === first);
  if (!entry) return null;
  return { key: entry, lang, rest: others.length ? others.join('/') : undefined };
}

/** The same page in the other language, for the language switch and hreflang tags. */
export function altPath(pathname: string): { lang: Lang; path: string } | null {
  const m = matchPath(pathname);
  if (!m) return null;
  const other: Lang = m.lang === 'nl' ? 'en' : 'nl';
  return { lang: other, path: pathFor(m.key, other, m.rest) };
}
